import setSpreadBackgroundsOperation from 'editor/src/store/design/operation/setSpreadBackgroundsOperation';
import setSpreadForegroundsOperation from 'editor/src/store/design/operation/setSpreadForegroundsOperation';
import { setDesignDataFromVariantSwitchAction } from 'editor/src/store/design/slice';
import { DesignData } from 'editor/src/store/design/types';
import setCurrentSpreadIndexOperation from 'editor/src/store/editor/operation/setCurrentSpreadIndexOperation';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';
import getDesignKey, { getDesignKeyFromDesign } from 'editor/src/store/variants/helpers/getDesignKey';
import { getSizeSpecificAreas, getVariationSize } from 'editor/src/store/variants/helpers/sizeSpecificAreas';
import { getDisplayedSize, getSizeVariant } from 'editor/src/store/variants/selector/getSizeSpecificSizes';
import { setDisplayedSizeAction } from 'editor/src/store/variants/slice';

import { batch } from '../../batchedSubscribeEnhancer';

import sendProductSwitchedOperation from './sendProductSwitchedOperation';

// GAPI-30848 — Guard against silently downgrading the active design to a
// slim saved subset of the same semantic variant.
//
// Background: for multi-tech apparel (Bella+Canvas 8800 in particular) the
// host pushes TWO designs that describe the same Color × Size × Tech variant
// under different productUids:
//   1. The customer's saved design (UUID-style productUid, e.g.
//      `de_gcsb_apparel_76e08e00-…`). Only carries spreads the customer has
//      actually placed artwork on — for embroidery that's often just
//      `chest-center-embroidery`.
//   2. The catalogue's full template (area-encoded productUid). Ships every
//      print area as a spread placeholder — both `chest-center-embroidery`
//      and `chest-left-embroidery`.
// The active `state.design.designData` ends up as #2 (template-augmented)
// after templates load, while `variationGroups[*].variationsInfo[*].designData`
// keeps a reference to #1 (because they have different `designKey`s so the
// template-reflect branch in `applyTemplatesIfNeededOperation` doesn't match).
// On a multi-option toggle (e.g. ticking a Size checkbox) this op replays
// the variantInfo's #1 designData onto the active design and the strip loses
// the un-saved spread placeholders.
//
// This guard short-circuits ONLY the strict-subset case where the active
// design is provably a superset of the candidate (every candidate spread
// name appears in current, current has at least one extra) AND the two
// don't contradict each other on dimensions (we accept missing dimensions
// on either side as "unknown — don't block"). Every other path — different
// dimensions on both sides, any non-subset spread, or candidate-has-equal-
// or-more-spreads — still swaps as before.
function isSlimSubsetOf(candidate: DesignData, current: DesignData): boolean {
  // Dimensions are an OPTIONAL safety check — many design payloads we observe
  // in the wild (notably the customer-saved variant payloads on Bella+Canvas
  // 8800) carry neither `related_dimensions` nor `dimensions` at the top
  // level. Treat "missing on either side" as "no contradiction" rather than
  // failing the guard, so the spread-name subset check below can still apply.
  const candDim = candidate.related_dimensions || candidate.dimensions;
  const currDim = current.related_dimensions || current.dimensions;
  if (candDim && currDim && (candDim.width !== currDim.width || candDim.height !== currDim.height)) {
    return false;
  }
  const candSpreads = candidate.spreads ?? [];
  const currSpreads = current.spreads ?? [];
  if (candSpreads.length >= currSpreads.length) {
    return false;
  }
  const currNames = new Set(currSpreads.map((s) => s?.name).filter(Boolean) as string[]);
  return candSpreads.every((s) => !!s?.name && currNames.has(s.name));
}

/** `size` picks which of the group's sizes to show; without it a product with per-size artwork stays on its size. */
const displaySelectedVariationOperation = (size?: string) => (dispatch: ThunkDispatch, getState: () => RootState) =>
  batch(() => {
    const state = getState();
    const { variationGroups, spreadImages, selectedGroupKey } = state.variants;
    const selectedGroup = variationGroups.find((group) => group.key === selectedGroupKey);
    if (!selectedGroup) {
      return;
    }

    const wantedSize =
      size ?? (getSizeSpecificAreas(state.variants.product).length ? getDisplayedSize(state) : undefined);
    const sizedVariant = getSizeVariant(state, selectedGroup, wantedSize);
    // A size chip that showed another size's design would pass that artwork off as this size's.
    if (size && !sizedVariant) {
      return;
    }
    const variant =
      sizedVariant ||
      selectedGroup.variationsInfo.find((variantInfo) => !!variantInfo.designData) ||
      selectedGroup.variationsInfo[0];

    const variantSize = getVariationSize(variant.variation, state.variants.product)?.value;
    if (variantSize !== state.variants.displayedSize) {
      dispatch(setDisplayedSizeAction(variantSize));
    }

    if (variant.designData) {
      const current = state.design.designData;
      // GAPI-30848 — see `isSlimSubsetOf` comment above. Skip the replace so
      // the active template-augmented design stays on screen; the strip's
      // print-area tiles remain rendered for the unsaved areas.
      if (
        current &&
        current.product_uid === variant.designData.product_uid &&
        isSlimSubsetOf(variant.designData, current)
      ) {
        // Spread images for the candidate variant still need refreshing in
        // case the host shipped per-variant backgrounds; fall through to the
        // spreadImages block below by NOT returning early.
      } else {
        const currentDesignKey = current ? getDesignKeyFromDesign(current) : undefined;
        const newDesignKey = getDesignKeyFromDesign(variant.designData);
        if (newDesignKey !== currentDesignKey) {
          dispatch(sendProductSwitchedOperation(variant));
        }

        // GAPI-30848 — preserve the active spread by name across a variant
        // chip click. The raw `setDesignDataAction` below doesn't touch
        // `state.editor.currentSpreadIndex`; if the new variant's spreads
        // are in a different order or length than the previous variant's,
        // the stale index points at the wrong spread (or is out of range),
        // and the bottom-strip tile-highlight can't find a matching active
        // spread — the customer sees no active print area after clicking
        // a chip. We compute the target index from the previously-active
        // spread NAME (which is stable across colour variants for the same
        // multi-tech combo) and dispatch the index update BEFORE
        // setDesignDataAction so React doesn't paint an inconsistent
        // intermediate frame.
        const prevSpreadName = current?.spreads?.[state.editor.currentSpreadIndex]?.name;
        const nextIdx = prevSpreadName ? variant.designData.spreads.findIndex((s) => s?.name === prevSpreadName) : -1;
        if (nextIdx >= 0 && nextIdx !== state.editor.currentSpreadIndex) {
          dispatch(setCurrentSpreadIndexOperation(nextIdx));
        } else if (nextIdx < 0 && state.editor.currentSpreadIndex >= variant.designData.spreads.length) {
          dispatch(setCurrentSpreadIndexOperation(0));
        }

        dispatch(setDesignDataFromVariantSwitchAction(variant.designData));
      }
    }

    const spreadImage = spreadImages[getDesignKey(variant.variation.productUid, variant)];
    if (spreadImage) {
      void dispatch(
        setSpreadBackgroundsOperation({
          productUid: variant.variation.productUid,
          spreads: spreadImage.spreadBackgrounds,
        }),
      );
      void dispatch(
        setSpreadForegroundsOperation({
          productUid: variant.variation.productUid,
          spreads: spreadImage.spreadForegrounds,
        }),
      );
    }
  });

export default displaySelectedVariationOperation;
